import { expected, isUndefined } from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useGetTkv2OrdersAPI } from '../../hooks/api/schema-hooks/useGetTkv2OrdersAPI.ts';
import {
  fetchGetBlob,
  fetchGetReportAPI,
  useUpdateTkv2OrderAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { Tkv2OrderManagementModule } from '../../modules/orderManagement/Tkv2OrderManagementModule.tsx';
import { TkOrderListing } from '../../modules/orderManagement/components/OrderTable/types.ts';
import { OrderID } from '../../modules/orderManagement/types.ts';
import { tkv2ToOrderListing } from './tkv2ToOrderListing.tsx';
import { getConfig } from '../../utils/getConfig.ts';

const getOpenOrderRoute = (
  version: 'tkv2' | 'legacy',
  requiresPrefill: boolean,
  orderID: OrderID,
  inspectifyOrder: boolean,
) => {
  if (version === 'legacy') return `/v1/order/${orderID}/setup`;

  if (requiresPrefill) return `/prepareOrder/${orderID}`;

  if (inspectifyOrder) return `/order/${orderID}/paired-cost/details`;

  return `/order/${orderID}/paired-cost/analysis`;
};

export const Tkv2OrdersListRouteElement = () => {
  const tkv2Orders = useGetTkv2OrdersAPI({});
  const qc = useQueryClient();
  const updateTkv2Order = useUpdateTkv2OrderAPI({ meta: { showGlobalLoader: true } });
  const { fetcherOptions } = useToolkitContext();

  if (tkv2Orders.isInitialLoading || isUndefined(tkv2Orders.data)) return null;

  const tkv2OrderListings: TkOrderListing[] = tkv2Orders.data.results.map((order) => {
    const toggleArchive = (isArchived: boolean) => {
      const { createdAt, id, modifiedAt, subjectAddress, ...restOfOrder } = order;
      const args = {
        pathParams: { orderID: expected(order.id) },
        body: { ...restOfOrder, isArchived },
      };
      return updateTkv2Order.mutate(args, {
        onSuccess: () => {
          qc.invalidateQueries(['api', 'v2', 'orders']);
        },
      });
    };

    const deleteOrder = () => {
      const { createdAt, id, modifiedAt, subjectAddress, ...restOfOrder } = order;
      const args = {
        pathParams: { orderID: expected(order.id) },
        body: { ...restOfOrder, isDeleted: true },
      };
      return updateTkv2Order.mutate(args, {
        onSuccess: () => {
          qc.invalidateQueries(['api', 'v2', 'orders']);
        },
      });
    };

    const downloadReport = async () => {
      const { id, subjectAddress } = order;
      const reportAttachment = await fetchGetReportAPI({
        ...fetcherOptions,
        pathParams: { orderID: id },
      });
      const blobId = reportAttachment?.body?.id;
      if (blobId) {
        const blob = await fetchGetBlob({ ...fetcherOptions, pathParams: { blobID: blobId } });
        const file = new File(
          [blob as BlobPart],
          `${subjectAddress?.AddressLineText?.replace(' ', '_')}_appraisal_report.pdf`,
          { type: blob.type },
        );
        const element = document.createElement('a');
        element.setAttribute('href', window.URL.createObjectURL(file));
        element.setAttribute('download', file.name);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
      }
    };

    const openDocumentsPage = () => {
      const { input } = order;
      const { orderNumber } = input;
      const marketplaceBaseUrl = getConfig('INSPECTIFY_MARKETPLACE_BASE_URL');
      const url = `${marketplaceBaseUrl}/orders/${orderNumber}/result`;

      window.open(url, '_blank');
      window.focus();
    };

    return {
      ...tkv2ToOrderListing(order),
      toggleArchive,
      deleteOrder,
      downloadReport,
      openDocumentsPage,
    };
  });

  return (
    <Tkv2OrderManagementModule orders={tkv2OrderListings} getOpenOrderRoute={getOpenOrderRoute} />
  );
};
